import React from 'react';
import { Box, Typography, Paper, useTheme } from '@mui/material';
import {
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as SuccessIcon,
  Help as HelpIcon
} from '@mui/icons-material';

const InfoBox = ({ 
  type = 'info', 
  title, 
  children, 
  icon: CustomIcon,
  sx = {} 
}) => {
  const theme = useTheme();
  
  // Define color configurations for both light and dark modes
  const typeConfig = {
    info: {
      icon: InfoIcon,
      light: {
        color: '#0288d1',
        bgColor: '#e1f5fe',
        borderColor: '#4fc3f7'
      },
      dark: {
        color: '#81d4fa',
        bgColor: 'rgba(3, 169, 244, 0.15)',
        borderColor: '#0288d1'
      }
    },
    warning: {
      icon: WarningIcon,
      light: {
        color: '#f57c00',
        bgColor: '#fff3e0',
        borderColor: '#ffb74d'
      },
      dark: {
        color: '#ffb74d',
        bgColor: 'rgba(255, 152, 0, 0.15)',
        borderColor: '#f57c00'
      }
    },
    error: {
      icon: ErrorIcon,
      light: {
        color: '#d32f2f',
        bgColor: '#ffebee',
        borderColor: '#ef5350'
      },
      dark: {
        color: '#ef5350',
        bgColor: 'rgba(244, 67, 54, 0.15)',
        borderColor: '#d32f2f'
      }
    },
    success: {
      icon: SuccessIcon,
      light: {
        color: '#388e3c',
        bgColor: '#e8f5e9',
        borderColor: '#66bb6a'
      },
      dark: {
        color: '#66bb6a',
        bgColor: 'rgba(76, 175, 80, 0.15)',
        borderColor: '#388e3c'
      }
    },
    help: {
      icon: HelpIcon,
      light: {
        color: '#7b1fa2',
        bgColor: '#f3e5f5',
        borderColor: '#ba68c8'
      },
      dark: {
        color: '#ce93d8',
        bgColor: 'rgba(186, 104, 200, 0.15)',
        borderColor: '#7b1fa2'
      }
    }
  };

  const config = typeConfig[type] || typeConfig.info;
  const IconComponent = CustomIcon || config.icon;
  const colorConfig = theme.palette.mode === 'dark' ? config.dark : config.light;
  
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 3,
        borderRadius: 1,
        backgroundColor: colorConfig.bgColor,
        borderLeft: '4px solid',
        borderColor: colorConfig.borderColor,
        boxShadow: theme.palette.mode === 'dark' ? '0 2px 10px rgba(0,0,0,0.2)' : 'none',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: theme.palette.mode === 'dark' ? '0 4px 15px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.1)',
        },
        ...sx
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        <IconComponent 
          sx={{ 
            mr: 1.5, 
            mt: 0.3,
            color: colorConfig.color,
            fontSize: '1.25rem',
            filter: theme.palette.mode === 'dark' ? 'drop-shadow(0 0 1px rgba(255,255,255,0.5))' : 'none'
          }} 
        />
        <Box sx={{ flex: 1 }}>
          {title && (
            <Typography 
              variant="subtitle1" 
              component="div"
              sx={{ 
                fontWeight: 'bold', 
                color: colorConfig.color,
                mb: 0.5
              }}
            >
              {title}
            </Typography>
          )}
          <Typography 
            variant="body2" 
            component="div"
            sx={{ color: theme.palette.text.primary }}
          >
            {children}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default InfoBox;